import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { SessionEndModal } from 'src/components/modals';
import { makeLogout } from 'src/utils/auth';
import { useTokenRefresh } from './useTokenRefresh';
import DebugPanel from './DebugPanel';
import {
  DEBUG_MODE,
  TEST_MODE,
  TIME_UNIT,
  INACTIVITY_LOGOUT_TIME,
  ACTIVITY_CHECK_INTERVAL,
  WARNING_THRESHOLDS,
  ACTIVITY_EVENTS,
  log,
} from './types';

const LOG_CTX = 'Inactivity';

/**
 * SessionManager - Handles:
 * 1. Proactive token refresh before expiry
 * 2. Inactivity timeout with warnings
 * 3. Optional debug panel for development
 */
const SessionManager: React.FC = () => {
  const { logout: logoutAuth0, isAuthenticated } = useAuth0();

  const { tokenInfo, refreshCount, lastRefresh, refreshLog, error, isRefreshing, forceRefresh } = useTokenRefresh();

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(30);
  const lastActivityRef = useRef(Date.now());
  const lastWarningThresholdRef = useRef<number | null>(null);
  const isWarningOpenRef = useRef(isWarningOpen);
  isWarningOpenRef.current = isWarningOpen;

  const updateActivity = useCallback(() => {
    // Use ref to avoid stale closure - ensures we always read current warning state
    if (isWarningOpenRef.current) {
      log(LOG_CTX, '✅ Activity detected, warning dismissed');
      setIsWarningOpen(false);
      lastWarningThresholdRef.current = null;
    }
    lastActivityRef.current = Date.now();
  }, []);

  const performLogout = useCallback(() => {
    makeLogout({ noEvents: true });
    logoutAuth0({
      logoutParams: { returnTo: `${window.location.protocol}//${window.location.host}/auth` },
    });
  }, [logoutAuth0]);

  const checkInactivity = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeUntilLogout = INACTIVITY_LOGOUT_TIME - timeSinceActivity;
    const unitsLeft = Math.max(0, Math.ceil(timeUntilLogout / TIME_UNIT));
    const unit = TEST_MODE ? 's' : 'm';

    log(LOG_CTX, `🔍 Idle: ${Math.round(timeSinceActivity / TIME_UNIT)}${unit}, remaining: ${unitsLeft}${unit}`);

    if (timeSinceActivity >= INACTIVITY_LOGOUT_TIME) {
      log(LOG_CTX, '⏰ Timeout reached, logging out...');
      performLogout();
      return;
    }

    const matchingThreshold = WARNING_THRESHOLDS.find((el) => unitsLeft <= el);

    if (matchingThreshold && matchingThreshold !== lastWarningThresholdRef.current) {
      lastWarningThresholdRef.current = matchingThreshold;
      setMinutesRemaining(unitsLeft);
      setIsWarningOpen(true);
      log(LOG_CTX, `⚠️ Warning shown (${unitsLeft}${unit} remaining)`);
    } else if (isWarningOpenRef.current) {
      setMinutesRemaining(unitsLeft);
    }
  }, [isAuthenticated, performLogout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceActivity = Date.now() - lastActivityRef.current;
        const timeUntilLogout = INACTIVITY_LOGOUT_TIME - timeSinceActivity;
        const unitsLeft = Math.ceil(timeUntilLogout / TIME_UNIT);
        if (unitsLeft > WARNING_THRESHOLDS[0]) {
          updateActivity();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, updateActivity]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(checkInactivity, ACTIVITY_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, checkInactivity]);

  const handleCloseWarning = () => {
    updateActivity();
    setIsWarningOpen(false);
  };

  const expirationTime = new Date(Date.now() + minutesRemaining * TIME_UNIT)
    .toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      second: TEST_MODE ? '2-digit' : undefined,
      hour12: true,
    })
    .toUpperCase();

  return (
    <>
      {isWarningOpen && (
        <SessionEndModal
          onClose={handleCloseWarning}
          minutesRemaining={minutesRemaining}
          expirationTime={expirationTime}
        />
      )}

      {DEBUG_MODE && (
        <DebugPanel
          tokenInfo={tokenInfo}
          refreshCount={refreshCount}
          lastRefresh={lastRefresh}
          refreshLog={refreshLog}
          error={error}
          isRefreshing={isRefreshing}
          onForceRefresh={forceRefresh}
          lastActivity={lastActivityRef.current}
        />
      )}
    </>
  );
};

export default SessionManager;
